
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { themes, Split, Notes } from "mdx-deck";
import { RoomAvailable, RoomAvailableHook, TwoOneSplit } from "./components";
export const theme = { ...themes.swiss,
  ...themes.prism,
  styles: { ...themes.swiss.styles,
    code: { ...themes.swiss.styles.code,
      fontSize: "1.4rem"
    }
  }
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};

const layoutProps = {
  theme
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1>{`Outclassed`}</h1>
    <h2>{`An Introduction to React Hooks`}</h2>
    <p>{`a Lunch & Learn
by Katie & Frank`}</p>
    <p>{`Slides are available at: `}<inlineCode parentName="p">{`hooks-slides.frank.dev`}</inlineCode></p>
    <hr></hr>
    <h1>{`Who is this for?`}</h1>
    <p>{`You know some React.`}</p>
    <p>{`You've probably heard of Hooks, but you want to know more about them.`}</p>
    <img style={{
      height: "auto",
      maxWidth: "30rem"
    }} src="https://media.giphy.com/media/B4ZgcoPTHYXL2/giphy.gif" />
    <Notes mdxType="Notes">
  <p>Katie's Experience: Before Apollo</p>
  <p>Katie's Experience: During Apollo</p>
  <p>Questions - please ask, we can open a code sandbox</p>
    </Notes>
    <hr></hr>
    <h1>{`The heck are hooks?`}</h1>
    <p>{`They are functions.`}</p>
    <p>{`They let you use state and other React features without writing a class.`}</p>
    <Notes mdxType="Notes">Frank</Notes>
    <hr></hr>
    <h1>{`Why do they exist?`}</h1>
    <p>{`Sometimes React is annoying:`}</p>
    <ul>
      <li parentName="ul">{`complicated & long class components`}</li>
      <li parentName="ul">{`complicated patterns like "Render Props"`}</li>
      <li parentName="ul">{`switching between class & function components`}</li>
    </ul>
    <p>{`They also can make code more "reusable".`}</p>
    <Notes mdxType="Notes">
  <p>Frank</p>
  <p>takeaway: avoid using classes</p>
  <p>i haven't written a class in over a year</p>
  <p>a smoother developer experience</p>
    </Notes>
    <hr></hr>
    <h1>{`The Hooks`}</h1>
    <p>{`React provides some hooks.`}</p>
    <p>{`We will be showing the 3 basic hooks.`}</p>
    <p>{`But you can write your own, too.`}</p>
    <Notes mdxType="Notes">
  <p>Pattern will be: introduce a hook, show before and after</p>
  <p>Context will be <a href="https://rooms.frank.dev">room status app</a></p>
    </Notes>
    <hr></hr>
    <h1>{`useState`}</h1>
    <p><em parentName="p">{`Basic Hook #1 of 3`}</em></p>
    <p>{`Returns a stateful value, and a function to update it.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const [state, setState] = useState(initialState);
`}</code></pre>
    <Notes mdxType="Notes">
  <p>React state without a class.</p>
  <p>Each piece of state has it's own setter.</p>
    </Notes>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// before useState hook
export class RoomAvailable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAvailable: props.room.isAvailable
    };
  }
  render() {
    return (
      <div>
        <div>{this.state.isAvailable ? "OPEN" : "Not Available"}</div>
        <button onClick={() => this.setState({ isAvailable: false })}>
          Book
        </button>
      </div>
    );
  }
}
`}</code></pre>
    <Notes mdxType="Notes">
  <p>Our examples involve room availability</p>
  <p>What does this do?</p>
    </Notes>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// after useState hook
import React, { useState } from "react";

function RoomAvailable({ room }) {
  const [isAvailable, setIsAvailable] = useState(room.isAvailable);

  return (
    <div>
      <div>{isAvailable ? "OPEN" : "Not Available"}</div>
      <button onClick={() => setIsAvailable(false)}>Book</button>
    </div>
  );
}
`}</code></pre>
    <Notes mdxType="Notes">
  <p>Katie's initial confusion</p>
  <p>Initial reactions, questions?</p>
  <p>you could create multiple states here</p>
  <p>how this works in typescript?</p>
    </Notes>
    <hr></hr>
    <h1>{`useEffect`}</h1>
    <p><em parentName="p">{`Basic Hook #2 of 3`}</em></p>
    <p>{`A place to put side effects like mutations, fetching, subscriptions, timers, etc.`}</p>
    <p>{`Runs after every component render. Replaces class lifecycle methods.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`useEffect(() => {
  // do this thing after render
});
`}</code></pre>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// updating the document title
// before useEffect hook
import React from "react";

export class RoomPage extends React.Component {
  componentDidUpdate() {
    document.title = this.props.room.name;
  }
  render() {
    return <div>{props.children}</div>;
  }
}
`}</code></pre>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// updating the document title
// after useEffect hook
import React, { useEffect } from "react";

function RoomPage({ room, children }) {
  useEffect(() => {
    document.title = room.name;
  }, [room]);

  return <div>{children}</div>;
}
`}</code></pre>
    <Notes mdxType="Notes">Notice dependency array.</Notes>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// fetching data on mount
// before useEffect hook
export class RoomList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rooms: []
    };
  }
  componentDidMount() {
    fetch("/api/rooms").then(rooms => {
      this.setState({ rooms });
    });
  }

  // render room list...
}
`}</code></pre>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// fetching data on mount
// after useEffect hook
function RoomList() {
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    fetch("/api/rooms").then(rooms => {
      setRooms(rooms);
    });
  }, []);

  // render room list...
}
`}</code></pre>
    <Notes mdxType="Notes">Notice how you can group code with shared purpose.</Notes>
    <hr></hr>
    <h1>{`Writing Custom Hooks`}</h1>
    <p>{`Intra-component logic is now composable.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`function useRooms() {
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    fetch("/api/rooms").then(rooms => {
      setRooms(rooms);
    });
  }, []);
  return rooms;
}

// useRooms hook can be used by RoomList component
`}</code></pre>
    <hr></hr>
    <h1>{`Demo`}</h1>
    <p><a parentName="p" {...{
        "href": "https://github.com/frankalbenesius/conference-room-availability/blob/master/src/hooks/useTimeStr.js"
      }}><inlineCode parentName="a">{`useTimeStr`}</inlineCode></a>{` - returns a self-updating current time string`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/frankalbenesius/conference-room-availability/blob/master/src/hooks/useRooms.js"
      }}><inlineCode parentName="a">{`useRooms`}</inlineCode></a>{` - returns a fetched list of rooms`}</p>
    <hr></hr>
    <h1>{`useContext`}</h1>
    <p><em parentName="p">{`Basic Hook #3 of 3`}</em></p>
    <p>{`Accepts a context object and returns the current context value for that context.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const MyContext = React.createContext("some value");

// and then, in a function component:
const value = useContext(MyContext); // "some value"
`}</code></pre>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// import your context
import { RoomsState } from "../RoomsContext";

// useContext from a component
function RoomCount() {
  const rooms = useContext(RoomsState);
  return <div>{rooms.length}</div>;
}

// useContext from a hook
function useRoomsCount() {
  const rooms = useContext(RoomsState);
  return rooms.length;
}
`}</code></pre>
    <hr></hr>
    <h1>{`useReducer`}</h1>
    <p><em parentName="p">{`An alternative to useState. `}</em></p>
    <p>{`Accepts a reducer and returns the current state paired with a dispatch method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const [state, dispatch] = useReducer(reducer, initialState);
`}</code></pre>
    <hr></hr>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`function reducer(state, action) {
  switch (action.type) {
    case "increment":
      return state + 1;
    case "decrement":
      return state - 1;
    default:
      throw new Error();
  }
}
function Counter() {
  const [state, dispatch] = useReducer(reducer, 0);
  return (
    <div>
      Count: {state.count}
      <button onClick={() => dispatch({ type: "decrement" })}>-</button>
      <button onClick={() => dispatch({ type: "increment" })}>+</button>
    </div>
  );
}
`}</code></pre>
    <hr></hr>
    <h1>{`useRedux (kind of)`}</h1>
    <p><inlineCode parentName="p">{`useContext`}</inlineCode>{` combined with `}<inlineCode parentName="p">{`useReducer`}</inlineCode>{` makes a pseudo-redux.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import {RoomsState, RoomsDispatch} from "../RoomsContext";

function RoomsList() {
  // we can access both the state and the dispatch function
  const rooms = useContext(RoomsState);
  const dispatch = useContext(RoomsDispatch);

  ...
}
`}</code></pre>
    <hr></hr>
    <h1>{`Demo`}</h1>
    <p><a parentName="p" {...{
        "href": "https://github.com/frankalbenesius/conference-room-availability/blob/master/src/components/DarkModeProvider.js"
      }}><inlineCode parentName="a">{`useReducer + useContext`}</inlineCode></a>{` - for a "dark mode" context`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/frankalbenesius/conference-room-availability/blob/master/src/hooks/useRoomAvailability.js"
      }}><inlineCode parentName="a">{`useRoomAvailability`}</inlineCode></a>{` - a custom hook that uses another custom hook`}</p>
    <hr></hr>
    <h1>{`3rd Party Hooks`}</h1>
    <p>{`NPM ain't just for components anymore.`}</p>
    <ul>
      <li parentName="ul">{`easy debouncing with `}<a parentName="li" {...{
          "href": "https://github.com/xnimorz/use-debounce"
        }}>{`useDebounce`}</a></li>
      <li parentName="ul">{`css media queries with `}<a parentName="li" {...{
          "href": "https://github.com/streamich/use-media"
        }}>{`useMedia`}</a></li>
      <li parentName="ul">{`smart data fetching with `}<a parentName="li" {...{
          "href": "https://github.com/tannerlinsley/react-query"
        }}>{`react-query`}</a>{` and `}<a parentName="li" {...{
          "href": "https://github.com/zeit/swr"
        }}>{`swr`}</a></li>
    </ul>
    <hr></hr>
    <h1>{`Thank You`}</h1>
    <p>{`Resources:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://reactjs.org/docs/hooks-intro.html"
        }}>{`Introducing Hooks`}</a><ul parentName="li">
          <li parentName="ul">{`Highly recommend watching the video introduction.`}</li>
        </ul></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://reactjs.org/docs/hooks-reference.html"
        }}>{`Hooks API Reference`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://reactjs.org/docs/hooks-faq.html"
        }}>{`Hooks FAQ`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/rehooks/awesome-react-hooks"
        }}>{`awesome-react-hooks`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/frankalbenesius/conference-room-availability"
        }}>{`Rooms App Codebase`}</a></li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;